import {
  AuditInfo,
  AuditInfoDto,
  ChimneySensorPlacementStringEnum,
  FlueBurnMetricAggregateKeyFactory,
  FlueBurnMetricAggregateTimeUnit,
  FlueBurnMetricAggregateTimeUnitUtils,
  FlueId,
  FlueTypeStringsEnum,
} from "firefly-shared-ts-domain";
import { Fireplace, FireplaceDto } from "../fireplace/Fireplace";
import { SecureEntity, Typed } from "firefly-my-chimney-ts-shared";
import { DateTime } from "luxon";
import { IllegalStateError } from "shared-ts-utils-core";
import { Chimney } from "../Chimney";
import { DateTimeISO } from "shared-ts-types";
import { merge } from "lodash";
import {
  FlueBurnMetricAggregate,
  FlueBurnMetricAggregateDto,
} from "./metric/FlueBurnMetricAggregate";
import { FlueBurnMetricAggregateValuesDto } from "./metric/FlueBurnMetricAggregateValues";

export interface McFlueFieldsDto {
  name: string | null;
  mcName: string | null;
  type: FlueTypeStringsEnum;
  sensorPlacement: ChimneySensorPlacementStringEnum;
  shared?: boolean;
  height?: number;
  lastSweepDate?: DateTimeISO;
}

export interface FlueDto extends SecureEntity<FlueId>, Typed<"Flue"> {
  audit: AuditInfoDto;
  fields: McFlueFieldsDto;
  fireplaces: Array<FireplaceDto>;
  metrics: Array<FlueBurnMetricAggregateDto>;
}

export interface FlueCopyArgs {
  fields?: Partial<McFlueFieldsDto>;
  fireplaces?: Array<Fireplace>;
}

export interface Flue extends SecureEntity<FlueId>, Typed<"Flue"> {
  audit: AuditInfo;
  fields: McFlueFieldsDto;
  metrics: Array<FlueBurnMetricAggregate>;
  fireplaces: Array<Fireplace>;
  chimney: Chimney | undefined;
  setChimney: (chimney: Chimney) => void;
  addFireplace: (fireplace: Fireplace) => void;
  querySingleMetricsOrUndef: (
    timeUnit: FlueBurnMetricAggregateTimeUnit,
    time?: DateTime
  ) => FlueBurnMetricAggregate | undefined;
  querySingleMetricsOrDefault: <
    D extends FlueBurnMetricAggregateValuesDto | undefined
  >(
    timeUnit: FlueBurnMetricAggregateTimeUnit,
    time: DateTime | undefined,
    def: D
  ) => D extends undefined
    ? FlueBurnMetricAggregate | undefined
    : FlueBurnMetricAggregate;
  copy: (args: FlueCopyArgs) => Flue;
}

export class FlueImpl implements Flue {
  readonly type = "Flue";
  readonly id: FlueId;
  readonly securityToken: string;
  readonly audit: AuditInfo;
  readonly fields: McFlueFieldsDto;
  readonly metrics: Array<FlueBurnMetricAggregate>;
  readonly fireplaces: Array<Fireplace>;
  chimney: Chimney | undefined;

  constructor(args: {
    id: FlueId;
    securityToken: string;
    audit: AuditInfo;
    fields: McFlueFieldsDto;
    metrics: Array<FlueBurnMetricAggregateDto> | Array<FlueBurnMetricAggregate>;
    fireplaces?: Array<Fireplace>;
    chimney?: Chimney;
  }) {
    this.id = args.id;
    this.securityToken = args.securityToken;
    this.audit = args.audit;
    this.fields = args.fields;
    this.metrics =
      args.metrics.length === 0
        ? []
        : args.metrics[0] instanceof FlueBurnMetricAggregate
        ? (args.metrics as Array<FlueBurnMetricAggregate>)
        : args.metrics.map((it) =>
            it instanceof FlueBurnMetricAggregate
              ? it
              : new FlueBurnMetricAggregate(it)
          );
    this.fireplaces = args.fireplaces ?? [];
    this.chimney = args.chimney;
  }

  copy(args: FlueCopyArgs): Flue {
    const fields =
      args.fields !== undefined ? merge(this.fields, args.fields) : this.fields;
    const fireplaces = args.fireplaces ?? this.fireplaces;

    const copy = new FlueImpl({
      id: this.id,
      securityToken: this.securityToken,
      audit: this.audit,
      fields: fields,
      metrics: this.metrics,
      fireplaces: fireplaces,
      chimney: this.chimney,
    });
    copy.fireplaces.forEach((fireplace) => {
      fireplace.setFlue(copy);
    });
    return copy;
  }

  setChimney(chimney: Chimney) {
    this.chimney = chimney;
  }

  addFireplace(fireplace: Fireplace) {
    fireplace.setFlue(this);
    this.fireplaces.push(fireplace);
  }

  querySingleMetricsOrUndef(
    timeUnit: FlueBurnMetricAggregateTimeUnit,
    time: DateTime | undefined
  ): FlueBurnMetricAggregate | undefined {
    return this.querySingleMetricsOrDefault(timeUnit, time, undefined);
  }

  querySingleMetricsOrDefault<
    D extends FlueBurnMetricAggregateValuesDto | undefined
  >(
    timeUnit: FlueBurnMetricAggregateTimeUnit,
    time: DateTime | undefined,
    def: D
  ): D extends undefined
    ? FlueBurnMetricAggregate | undefined
    : FlueBurnMetricAggregate {
    let result: FlueBurnMetricAggregate[] = [];
    if (timeUnit.startsWith("@")) {
      result = this.metrics.filter((it) => {
        return timeUnit === it.timeUnit;
      });
    } else if (time != null) {
      const unit =
        FlueBurnMetricAggregateTimeUnitUtils.toDateTimeUnit(timeUnit);
      const timeStartOfUnit = time.startOf(unit).toUTC();

      result = this.metrics.filter((it) => {
        if (timeUnit !== it.timeUnit) {
          return false;
        }
        if (time === undefined) {
          return true;
        }
        if (it.time === undefined) {
          return false;
        }
        return timeStartOfUnit.equals(it.time.toUTC());
      });
    }

    if (result.length === 0) {
      return (def === undefined
        ? undefined
        : new FlueBurnMetricAggregate({
            key: FlueBurnMetricAggregateKeyFactory.create(
              this.id,
              timeUnit,
              time
            ),
            flueId: this.id,
            timeUnit: timeUnit,
            time: time,
            values: def,
          })) as unknown as D extends undefined
        ? FlueBurnMetricAggregate | undefined
        : FlueBurnMetricAggregate;
    } else if (result.length === 1) {
      return result[0];
    } else {
      throw new IllegalStateError(
        "Unexpected number of elements in result, expected 0 or 1, got: " +
          result.length
      );
    }
  }
}
